<template>
  <div class="detalle-servicio-panel">
    <DxTabPanel height="95%" tabs-position="top" icon-position="left" :defer-rendering="false">
      <DxItem :key="1" :title="tituloTab1" class="tab-item" :disabled="false" icon="fa-regular fa-file-lines">
        <servicio-energia-tab-datos :model="model" :tiene-tramites-asociados="tieneTramitesAsociados" ref="tabDatos"></servicio-energia-tab-datos>
      </DxItem>
      <DxItem :key="2" :title="tituloTab2" class="tab-item" :disabled="false" icon="fa-regular fa-file-signature">
        <servicio-energia-tab-plantilla :model="model" :tiene-tramites-asociados="tieneTramitesAsociados" ref="tabAnexo"></servicio-energia-tab-plantilla>
      </DxItem>
      <DxItem :key="3" :title="tituloTab3" class="tab-item" :disabled="false" icon="fa-regular fa-percent">
        <servicio-energia-tab-descuentos :model="model" :tiene-tramites-asociados="tieneTramitesAsociados" ref="tabDescuentos"></servicio-energia-tab-descuentos>
      </DxItem>
      <DxItem :key="4" :title="tituloTab4" class="tab-item" :disabled="false" icon="fa-regular fa-euro-sign">
        <servicio-energia-tab-comisiones-panel :model="model" :is-edit-mode="isEditMode" ref="tabComisiones"></servicio-energia-tab-comisiones-panel>
      </DxItem>
    </DxTabPanel>
  </div>
</template>

<script setup lang="ts">
import ServicioEnergiaTabDatos from '@/components/servicios-energia/base/ServicioEnergiaTabDatos.vue';
import ServicioEnergiaTabPlantilla from '@/components/servicios-energia/base/SevicioEnergiaTabPlantilla.vue';
import ServicioEnergiaTabDescuentos from '@/components/servicios-energia/base/ServicioEnergiaTabDescuentos.vue';
import ServicioEnergiaTabComisionesPanel from '@/components/servicios-energia/base/ServicioEnergiaTabComisionesPanel.vue';
import { computed, getCurrentInstance, onMounted, ref } from 'vue';
import { type CreateComisionEnergiaOfertaModel, type CreateServicioEnergiaModel, type DetailServicioEnergiaModel, type EditServicioEnergiaModel } from '@/.shared/.api';
import DxTabPanel, { DxItem } from 'devextreme-vue/tab-panel';
import { useIntl } from '@/.shared/services/i18n/i18nservice';
import { useModalBuilder, type ToolbarReference } from '@/.shared/helpers/modal-building-helper';
import type { DxButton } from 'devextreme-vue';
import { messageBoxResult } from '@/.shared/components/dialogs/message-box';
import { toastError, toastSuccess } from '@/.shared/services/notification-service';
import validationEngine from 'devextreme/ui/validation_engine';
import { useApi } from '@/.shared/.api/integration';
import { toUTCDateOnly } from '@/.shared/helpers/date-parser-helper';
import { ApiError } from '@/.shared/services/http';
import { SERVICE_WITH_BAD_END_DATE } from '@/.shared/errors/codes';

const intl = useIntl();
const api = useApi();
const tabDatos = ref<InstanceType<typeof ServicioEnergiaTabDatos>>();
const tabAnexo = ref<InstanceType<typeof ServicioEnergiaTabPlantilla>>();
const tabDescuentos = ref<InstanceType<typeof ServicioEnergiaTabDescuentos>>();
const tabComisiones = ref<InstanceType<typeof ServicioEnergiaTabComisionesPanel>>();
const tabPanel = ref<InstanceType<typeof DxTabPanel>>();

const props = defineProps({
  model: {
    type: Object as () => DetailServicioEnergiaModel | CreateServicioEnergiaModel,
    required: true
  },
  isEditMode: {
    type: Boolean,
    required: true
  },
  tieneTramitesAsociados: {
    type: Boolean,
    required: true
  }
});

const tituloTab1 = computed(() => `${intl.i18n.t('servicios.form.datos')}`);
const tituloTab2 = computed(() => `${intl.i18n.t('servicios.form.plantilla_anexo')}`);
const tituloTab3 = computed(() => `${intl.i18n.t('servicios.form.descuentos')}`);
const tituloTab4 = computed(() => `${intl.i18n.t('servicios.form.comisiones')}`);

onMounted(async () => {
  const modal = useModalBuilder();
  modal.setContext(getCurrentInstance()!)

  configureButtons(modal);
});

function configureButtons(modal: ReturnType<typeof useModalBuilder>) {
  let okButton: ToolbarReference<DxButton>;
  let cancelButton: ToolbarReference<DxButton>;
  ({ okButton, cancelButton } = modal.standardButtons.addOkCancel());
  okButton.update(w => w.text = `${intl.i18n.t("shared.ui.buttons.save")}`);
  okButton.update(w => w.onClick = async () => {
    saveOrEdit().then((value) => {
      if (value)
        modal.close(messageBoxResult.ok)
    });
  }
  );
  cancelButton.update(w => w.onClick = () => modal.close(messageBoxResult.cancel));
}

const saveOrEdit = async () => {

  // Forzar el renderizado de las pestañas necesarias antes de validar
  if (tabPanel.value) {
    const instance = tabPanel.value.instance;
    const currentTabIndex = instance.option('selectedIndex');
    // Activar las pestañas que necesitan validación
    instance.option('selectedIndex', 1);
    instance.option('selectedIndex', 2);
    // Restaurar la pestaña original
    instance.option('selectedIndex', currentTabIndex);
  }

  var res1 = validationEngine.validateGroup("validacionServicioEnergia");
  var res2 = validationEngine.validateGroup("validacionTipoPersona");
  var res3 = validationEngine.validateGroup("validacionFechaFin");
  var res4 = await tabAnexo.value?.validate();
  var res6 = await tabDescuentos.value?.validate();

  if (res1.isValid && res2.isValid && res3.isValid && res4 && res6) {
    props.model.FechaInicio = toUTCDateOnly(props.model.FechaInicio);
    props.model.FechaFin = toUTCDateOnly(props.model.FechaFin);

    if (props.isEditMode) {
      if (documentoASubir.value)
        await api.callStandardApi("putAnexoServicioEnergia", { showLoading: false }, (props.model as EditServicioEnergiaModel).Id, documentoASubir.value?.name, documentoASubir.value)
          .catch(() => {
            toastError(intl.i18n.t('servicio_energia.edicion_anexo_fallo'));
            return false;
          });

        try {
          await api.callStandardApi("editServicioEnergia", { showLoading: true }, props.model as EditServicioEnergiaModel)
            .then(() => toastSuccess(intl.i18n.t('servicio_energia.edicion_exito')))
        } 
        catch(e: any) {
          if (e instanceof ApiError && e.details?.errorList.length === 1 && [SERVICE_WITH_BAD_END_DATE].includes(e.details.errorList[0].Code)) {
            toastError(intl.i18n.t(`shared.errors.api.${e.details.errorList[0].Code}`))
          }
          else {
            toastError(intl.i18n.t('servicio_energia.edicion_fallo'));
          }
          return false;
        }
    } else {
      if (documentoASubir.value) {
        await api.callStandardApi("postDocumentoTemp", { showLoading: false }, documentoASubir.value?.name, documentoASubir.value)
          .then(async (tempUrl: string) => {
            await api.callStandardApi("createServicioEnergia", { showLoading: true }, documentoASubir.value?.name, tempUrl, props.model as CreateServicioEnergiaModel)
              .then((idServicio: string) => {
                crearComisiones(idServicio);
                toastSuccess(intl.i18n.t('servicio_energia.creacion_exito'))
              })
              .catch(() => {
                toastError(intl.i18n.t('servicio_energia.creacion_fallo'));
                return false;
              });
          })
          .catch(() => {
            toastError(intl.i18n.t('servicio_energia.creacion_fallo'));
            return false;
          });
      } else {
        await api.callStandardApi("createServicioEnergia", { showLoading: true }, undefined, undefined, props.model as CreateServicioEnergiaModel)
          .then((idServicio: string) => {
            crearComisiones(idServicio);
            toastSuccess(intl.i18n.t('servicio_energia.creacion_exito'))
          })
          .catch(() => {
            toastError(intl.i18n.t('servicio_energia.creacion_fallo'));
            return false;
          });
      }
    }
    return true;
  } else {
    toastError(intl.i18n.t('servicio_energia.revise_validacion'));
  }

  return false;
}

const crearComisiones = async (idServicio: string) => {
  const comisiones = tabComisiones.value?.getComisiones();
  if (comisiones && comisiones.length > 0) {
    // Agrupar comisiones por idMarco
    const comisionesPorMarco = comisiones.reduce((acc, comision) => {
      const idMarco = comision.idMarco;
      if (!acc[idMarco]) {
        acc[idMarco] = [];
      }
      acc[idMarco].push(comision);
      return acc;
    }, {} as Record<string, typeof comisiones>);

    // Crear comisiones para cada marco
    for (const [idMarco, comisionesMarco] of Object.entries(comisionesPorMarco)) {
      await api.callStandardApi("createComisionesEnergiaServicio", {showLoading: false}, idServicio, idMarco, comisionesMarco as CreateComisionEnergiaOfertaModel[]);
    }
  }
};

const documentoASubir = computed(() => {
  return tabAnexo.value?.documentoASubir;
});

</script>